import React, { FC, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { debounce, partition } from 'lodash';
import classNames from 'classnames';
import qs from 'qs';
import {
  CountryType,
  GetSpecialitiesQuery,
  PaginationSortOrderEnum,
  PlaceTypeEnum,
  useGetCountriesQuery,
  useGetSpecialitiesQuery,
} from '@src/graphql/generated';
import { createLink } from '@src/util/createLink';
import { FEATURED_APPLICANTS_ROUTE } from '@src/Routes/links';
import { CountrySelect, RELOCATION_OPTION, REMOTE_OPTION } from '@src/containers/CountrySelect';
import { CORE_MARKET_COUNTRY_IDS } from '@src/constants/coreMarket';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { QueryParams } from '../interfaces/queryParams.interface';
import { SPECIALITY_TO_ICON } from '../constants/specialityIcons';
import './styles.scss';

type CountryItemType = Pick<CountryType, 'id' | 'longName' | 'shortName'>;

export const CategoriesSection: FC = () => {
  const history = useHistory();

  const location = useLocation();
  const queryParams: QueryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const { speciality: selectedSpecialityId, ...allSpecialitiesParams } = queryParams;
  const { place: { id: selectedPlaceId, name: selectedPlaceName } = {}, isRemote, isRelocation } = queryParams;

  const { data: countriesData, refetch: refetchCountries } = useGetCountriesQuery({
    variables: {
      paginationOptions: {
        sortOrder: PaginationSortOrderEnum.ASC,
      },
    },
  });
  const countries = countriesData?.countries.result || [];

  const [matched, remaining] = partition(countries, item => CORE_MARKET_COUNTRY_IDS.includes(item.id));
  const sortedCountries = [...matched, ...remaining];

  const foundCountry = sortedCountries.find(place => place.id === selectedPlaceId);

  const selectedCountry =
    foundCountry ||
    (selectedPlaceId === REMOTE_OPTION.id || isRemote
      ? REMOTE_OPTION
      : selectedPlaceId === RELOCATION_OPTION.id || isRelocation
      ? RELOCATION_OPTION
      : undefined);

  const { data: specialitiesData, loading: isSpecialitiesLoading } = useGetSpecialitiesQuery();
  const specialities = specialitiesData?.specialities;

  const specialitiesMap = specialities?.reduce(
    (acc, speciality) => {
      if (speciality?.id) {
        acc[speciality.id] = speciality;
      }

      return acc;
    },
    {} as Record<string, GetSpecialitiesQuery['specialities'][0]>,
  );

  const specialitiesWithNoIcon = { ...specialitiesMap };

  SPECIALITY_TO_ICON.forEach(speciality => {
    delete specialitiesWithNoIcon[speciality.id];
  });

  const onSearchCountry = useCallback(
    debounce((searchValue: string) => {
      refetchCountries({
        paginationOptions: {
          sortOrder: PaginationSortOrderEnum.ASC,
        },
        searchPattern: searchValue,
      });
    }, 350),
    [],
  );

  const createLinkWithParams = ({
    specialityId,
    country,
  }: {
    country: CountryItemType | undefined;
    specialityId: string | undefined;
  }) => {
    const params = { ...queryParams };

    if (country) {
      params['place'] = {
        id: country.id,
        name: country.longName,
        type: PlaceTypeEnum.COUNTRY,
      };
    }

    if (specialityId) {
      params['speciality'] = specialityId;
    }

    return createLink(FEATURED_APPLICANTS_ROUTE, { queryParams: params });
  };

  const sendMetrics = (categoryName: string) => {
    trackEvent({
      context: MetricContext.FEATURED_APPLICANTS_VIEW,
      interaction: MetricAction.SELECT_CATEGORY,
      payload: {
        Category: categoryName,
        Country: selectedCountry?.longName,
      },
    });
  };

  const onSelectCountry = (country: Pick<CountryType, 'id' | 'longName' | 'shortName'>): void => {
    const path = createLinkWithParams({
      country: country,
      specialityId: selectedSpecialityId,
    });
    history.replace(path);
  };

  return (
    <div className="start-headhunt-categories">
      <div className="start-headhunt-categories__title">
        Explore by categories
        {(!!selectedCountry?.longName || !!selectedPlaceName) && <>&nbsp;in&nbsp;</>}
        <CountrySelect
          className="start-headhunt-categories__country-select"
          countries={sortedCountries}
          onSearch={onSearchCountry}
          onSelectCountry={onSelectCountry}
          selectedCountry={selectedCountry}
          defaultDisplayValue={selectedPlaceName}
        />
      </div>

      <div className="start-headhunt-categories__specialities">
        <Link
          className={classNames({
            'start-headhunt-categories__speciality-item': true,
            'start-headhunt-categories__speciality-item--selected': !selectedSpecialityId,
          })}
          onClick={() => sendMetrics('All')}
          to={createLink(FEATURED_APPLICANTS_ROUTE, {
            queryParams: {
              ...allSpecialitiesParams,
            },
          })}
        >
          ✨&nbsp;All
        </Link>
        {!isSpecialitiesLoading &&
          SPECIALITY_TO_ICON.map(specialityItem => {
            const speciality = specialitiesMap?.[specialityItem.id];

            if (!speciality?.name) {
              return null;
            }

            return (
              <Link
                className={classNames({
                  'start-headhunt-categories__speciality-item': true,
                  'start-headhunt-categories__speciality-item--selected': specialityItem.id === selectedSpecialityId,
                })}
                key={specialityItem.id}
                onClick={() => sendMetrics(speciality.name)}
                to={createLink(FEATURED_APPLICANTS_ROUTE, {
                  queryParams: { ...queryParams, speciality: specialityItem.id },
                })}
              >
                {specialityItem.icon}
                &nbsp;
                {speciality.name}
              </Link>
            );
          })}

        {isSpecialitiesLoading &&
          SPECIALITY_TO_ICON.map(specialityItem => {
            return (
              <div
                className="start-headhunt-categories__speciality-item start-headhunt-categories__speciality-item--loading pulse-fade"
                key={specialityItem.id}
              />
            );
          })}
      </div>
    </div>
  );
};
